import React, { useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router";
import RecoErrorBoundary from "./RecoErrorBoundary";
import { RecoApis } from "apis/recoAPI";
import { useGlobalConfig } from "context/GlobalContext";
import { RootState } from "store/Store";
import { CMSPilotsReducerState } from "store/dataReducers/cmsPilotSlice";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { selectDraftRecos, selectInitDone, selectModifiedRecos, selectPublishedRecos, setRecos, setReviewWorkflowAction } from "store/dataReducers/recommendationsSlice";
import { BglLoading } from "components/loading";
import { selectRecoCommentsInitDone, setRecoCommentsCount } from "store/dataReducers/recoCommentsSlice";
import { RunContextApp } from "framework/RCApp";
import { APIData } from "types/cms/frontendTypes";
import { CommentsApis } from "apis/commentsAPI";

const RecoLayout = () => {

  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  
  const pilotState            = useSelector((state : RootState) => state.cmsPilot) as CMSPilotsReducerState;
  const recoInitDone          = useSelector(selectInitDone)
  const commentsInitDone      = useSelector(selectRecoCommentsInitDone)
  
  const draftRecos            = useSelector(selectDraftRecos)
  const publishedRecos        = useSelector(selectPublishedRecos)
  const modifiedRecos         = useSelector(selectModifiedRecos)

  const memoizedRows = useMemo(() => {
    const publishedRecosWithoutModifications = publishedRecos.filter(
      pubReco => modifiedRecos.find(modReco => modReco.documentId === pubReco.documentId) === undefined)
    return [...draftRecos, ...modifiedRecos, ...publishedRecosWithoutModifications]

  } ,[draftRecos, modifiedRecos, publishedRecos])

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    setError(error.message)
  };

  const getRecos = async () => {
    
    setIsLoading(true)

    const [reviewWorkflowResp, recoResp] = await Promise.all([
      RecoApis.getReviewWorkflow(rc),
      RecoApis.getRecosForUtility(rc, pilotState.selectedPilotDocId)
    ])

    if (reviewWorkflowResp.error || recoResp.error) {
      setError(reviewWorkflowResp.error || recoResp.error)
    } else {
      dispatch(setRecos({reco : recoResp.data, recoScore : pilotState.selectedPilotRecoScore}))
      dispatch(setReviewWorkflowAction(reviewWorkflowResp.data))
    }
    
    setIsLoading(false)      
  }

  const requestRecoCommentCount = async (rc : RunContextApp, rows : APIData<"api::recommendation.recommendation">[]) => {
    const docIds : string[] = rows.map(row => row.documentId)
    if (docIds.length === 0) return
    const resp = await CommentsApis.getCommentCount(rc, 'api::recommendation.recommendation', docIds)
    if (resp) {
      dispatch(setRecoCommentsCount((resp as any).counts))
    }
  }
  
  useEffect(() => {

    if (!recoInitDone) {
      getRecos()
    } else {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!commentsInitDone) {
      requestRecoCommentCount(rc, memoizedRows)
    }

  }, [memoizedRows])

  return (
    <React.Fragment>
      <RecoErrorBoundary onError={handleError}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><BglLoading /></Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            Error: {error}
          </Box>
        ) : (
          <Outlet />
        )}
      </RecoErrorBoundary>
    </React.Fragment>
  );
};

export default RecoLayout;